import { ComponentType, PureComponent } from 'react';
import { connect } from 'react-redux';
import { isFunction, noop, omit } from 'lodash/fp';
import type { Dispatch } from 'redux';

import LoadingErrorAlert, { shouldShowLoadingErrorAlert } from '../loading-error-alert';
import LoadingSpinner from '../loading-spinner';
import type { LoadingError } from '../loading-error-alert/types';
import type { RootState } from '../../reducers';

type RouteMatch = {
  params?: {
    id?: string | null;
  };
};

type DataLoaderProps = {
  dispatch: Dispatch;
  history?: {
    push: (path: string) => void;
  };
  isLoaded?: boolean;
  loadData?: Function | null; // DEPRECATED: not in use for server-side data fetching in NextJS
  loadingError?: LoadingError | null;
  match?: RouteMatch;
  state?: RootState;
};

export type DataLoaderOptions<Props = Record<string, unknown>> = {
  selectIsDataLoaded: (state: RootState, props: Props) => boolean;
  selectLoadingError: (state: RootState, props: Props) => LoadingError | null | undefined;
};

const defaultHistory = {
  push: noop,
};
const defaultMatch = {
  params: {
    id: null,
  },
};

const getDataLoaderPropsWithDefaults = (props: DataLoaderProps) => {
  const { history, isLoaded, loadingError, match } = props;

  return {
    ...props,
    history: history === undefined ? defaultHistory : history,
    isLoaded: isLoaded === undefined ? false : isLoaded,
    loadingError: loadingError === undefined ? null : loadingError,
    match: match === undefined ? defaultMatch : match,
  };
};

const dataLoader = <Props = Record<string, unknown>,>(
  options: DataLoaderOptions<Props> = {} as DataLoaderOptions<Props>,
) => {
  if (!isFunction(options.selectIsDataLoaded)) {
    throw new Error('selectIsDataLoaded option must be a function');
  }
  if (!isFunction(options.selectLoadingError)) {
    throw new Error('selectLoadingError option must be a function');
  }

  const mapStateToProps = (state: RootState, props: Props) => ({
    isLoaded: options.selectIsDataLoaded(state, props),
    loadingError: options.selectLoadingError(state, props),
    state,
  });

  const mapDispatchToProps = (dispatch: Dispatch) => ({
    dispatch,
  });

  return (WrappedComponent: ComponentType<Omit<DataLoaderProps, 'dispatch' | 'state'>>) => {
    class DataLoader extends PureComponent<DataLoaderProps> {
      // DEPRECATED: not in use for server-side data fetching in NextJS
      override UNSAFE_componentWillMount() {
        const { dispatch, history, loadData, match, state } = getDataLoaderPropsWithDefaults(this.props);

        // Define loadData prop in top-level routes config ('../../routes/index.jsx')
        if (isFunction(loadData)) {
          loadData(state, dispatch, match?.params, history);
        }
      }

      // DEPRECATED: not in use for server-side data fetching in NextJS
      override componentDidUpdate(prevProps: DataLoaderProps) {
        const { dispatch, history, loadData, match, state, isLoaded } = getDataLoaderPropsWithDefaults(this.props);
        const { isLoaded: prevIsLoaded } = getDataLoaderPropsWithDefaults(prevProps);

        if (prevIsLoaded && !isLoaded) {
          if (isFunction(loadData)) {
            loadData(state, dispatch, match?.params, history);
          }
        }
      }

      override render() {
        const props = getDataLoaderPropsWithDefaults(this.props);
        const { isLoaded, loadingError } = props;

        if (!isLoaded) {
          return <LoadingSpinner />;
        }

        const errorToShow: LoadingError | null | undefined = loadingError?.timeout
          ? { explanation: 'It seems our servers are taking too long to respond. Please try again later.' }
          : loadingError;

        if (shouldShowLoadingErrorAlert(errorToShow)) {
          return <LoadingErrorAlert loadingError={errorToShow as LoadingError} />;
        }

        return <WrappedComponent {...omit(['dispatch', 'state'], props)} />;
      }
    }

    return connect(mapStateToProps, mapDispatchToProps)(DataLoader);
  };
};

export default dataLoader;
